import { zodResolver } from '@hookform/resolvers/zod'
import { useLocalSearchParams, useNavigation } from 'expo-router'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Platform, StyleSheet } from 'react-native'
import { useCreateTag } from '@/hooks/tags'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { errorToString } from '@/lib/errors'
import { useReportNewlyCreated } from '@/lib/newlyCreatedRecord'
import { notify } from '@/lib/notice'
import { sanitizeReturnTo } from '@/lib/returnTo'
import { type TagFormData, tagSchema } from '@/types/tags'
import { Button } from '@/ui/Button'
import { Card } from '@/ui/Card'
import { FormErrorSummary } from '@/ui/form'
import { Page } from '@/ui/Page'
import { TagFormFields } from '@/ui/tags/TagFormFields'

export default function NewTagPage() {
    useAvoidSoftInputForScreen()
    const params = useLocalSearchParams<{ orgSlug: string; returnTo?: string }>()
    const returnTo = sanitizeReturnTo(params.returnTo, params.orgSlug)
    const { navigateBackTo } = useNavigateBack()
    const { mutation, isCreating } = useCreateTag()
    const reportNewlyCreated = useReportNewlyCreated()
    const navigation = useNavigation()

    useEffect(() => {
        if (returnTo && Platform.OS !== 'ios') {
            navigation.setOptions({
                headerLeft: () => (
                    <Button
                        icon="chevron-left"
                        title="Back"
                        onPress={() => navigateBackTo(returnTo)}
                    />
                ),
            })
        }
    }, [returnTo, navigation, navigateBackTo])

    const form = useForm<TagFormData>({
        resolver: zodResolver(tagSchema),
        defaultValues: {
            name: '',
            color: '#3B82F6',
            description: '',
            inactive: false,
        },
    })

    const handleSubmit = form.handleSubmit(async data => {
        try {
            const result = await mutation.mutateAsync(data)
            notify.success('Tag created successfully')
            if (returnTo) {
                reportNewlyCreated('tags', result.tagId)
                navigateBackTo(returnTo)
            } else {
                navigateBackTo(`/${params.orgSlug}/settings/tags`)
            }
        } catch (error) {
            const message = errorToString(error)
            form.setError('name', { type: 'manual', message })
        }
    })

    useHeaderOptions({
        buttons: {
            type: 'create',
            onPress: handleSubmit,
            loading: form.formState.isSubmitting || isCreating,
            loadingTitle: 'Creating...',
        },
    })

    return (
        <Page>
            <Card>
                <Card.Content style={styles.cardContent}>
                    <FormErrorSummary
                        errors={form.formState.errors}
                        isEnabled={form.formState.isSubmitted}
                    />
                    <TagFormFields control={form.control} />
                </Card.Content>
            </Card>
        </Page>
    )
}

const styles = StyleSheet.create({
    cardContent: {
        gap: 16,
    },
})
